"use client"

import { useEffect } from "react"
import { Button } from "@ui/shad"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import type {
  PublicIntakeFormBookingAttemptStatus,
  PublicIntakeFormDraft,
  PublicIntakeFormDraftPayload,
  PublicIntakeFormSchedulingAvailability,
  PublicIntakeFormSubmissionPayload,
  PublicIntakeFormSubmissionResult,
  PublicIntakeFormUpload,
} from "@/hooks/api/public-intake-forms"
import { useUnsavedChangesWarning } from "@/hooks/use-unsaved-changes-warning"

import { FormFrame } from "./form-frame"
import { useFormBridge, type FormBridgeInit, type FormBridgeTerminalState } from "./use-form-bridge"

interface FormRunnerProps {
  title: string
  localDraftKey?: string
  versionId: string
  init: FormBridgeInit
  html: { data: string | undefined; isLoading: boolean; error: Error | null; refetch: () => void }
  uploadFile: (input: { fieldKey: string; clientSubmissionId: string; file: File }) => Promise<PublicIntakeFormUpload>
  submitForm: (submission: PublicIntakeFormSubmissionPayload) => Promise<PublicIntakeFormSubmissionResult>
  fetchSchedulingAvailability: (fieldKey: string) => Promise<PublicIntakeFormSchedulingAvailability>
  pollBookingAttempt: (input: {
    attemptId: string
    clientSubmissionId: string
  }) => Promise<PublicIntakeFormBookingAttemptStatus>
  notice?: React.ReactNode
  onTerminal: (state: NonNullable<FormBridgeTerminalState>) => void
  remoteDraft?: PublicIntakeFormDraft | null
  saveDraft?: (draft: PublicIntakeFormDraftPayload) => Promise<PublicIntakeFormDraft>
  persistDocuments?: boolean
  draftSaveMode?: "automatic" | "manual"
  embedded?: boolean
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void
}

// Mount this keyed by the version it renders: a pinned version that retires
// mid-session is resolved by remounting on the current one, which also resets
// the bridge's handshake and submit state.
export function FormRunner({
  title,
  localDraftKey,
  versionId,
  init,
  html,
  uploadFile,
  submitForm,
  fetchSchedulingAvailability,
  pollBookingAttempt,
  notice,
  onTerminal,
  remoteDraft,
  saveDraft,
  persistDocuments,
  draftSaveMode,
  embedded,
  onUnsavedChangesChange,
}: FormRunnerProps) {
  const {
    frameProps,
    draftInfo,
    terminalState,
    hasUnsavedChanges,
    hasPendingDraftUploads,
    isSavingDraft,
    saveDraftNow,
  } = useFormBridge({
    localDraftKey,
    versionId,
    init,
    uploadFile,
    submitForm,
    fetchSchedulingAvailability,
    pollBookingAttempt,
    remoteDraft,
    saveDraft,
    persistDocuments,
    draftSaveMode,
  })

  useEffect(() => {
    if (terminalState) onTerminal(terminalState)
  }, [onTerminal, terminalState])

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  useEffect(
    () => () => {
      onUnsavedChangesChange?.(false)
    },
    [onUnsavedChangesChange]
  )

  const tabNotice = draftInfo.otherTabOpen ? "This form is open in another tab — the last change you make wins." : null
  useUnsavedChangesWarning(draftSaveMode === "manual" && hasUnsavedChanges, "You have unsaved intake form changes.")

  return (
    <FormFrame
      title={title}
      html={html.data}
      isLoading={html.isLoading}
      error={html.error}
      onRetry={html.refetch}
      frameProps={frameProps}
      notice={notice ?? tabNotice}
      embedded={embedded}
      footer={
        draftSaveMode === "manual" ? (
          <div className="bg-background flex shrink-0 items-center justify-end border-t px-6 py-3">
            <Button
              onClick={() =>
                void saveDraftNow().catch(() => toast.error("Changes couldn’t be saved. Please try again."))
              }
              disabled={!hasUnsavedChanges || hasPendingDraftUploads || isSavingDraft}
            >
              {isSavingDraft && <Loader2 className="animate-spin" />}
              Save changes
            </Button>
          </div>
        ) : undefined
      }
    />
  )
}
